import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';

export interface ActiveStep {
  journeyIndex: number;
  journeyTitle: string;
  sectionTitle: string;
  stepIndex: number;
  totalSteps: number;
  stepTitle: string;
  stepContent: ReactNode;
  /** CSS selector or data attribute to find the target element for spotlight */
  targetSelector?: string;
  placement?: 'left' | 'right' | 'top' | 'bottom';
  /** Route to navigate to before showing this step (e.g. '/catalog') */
  navigateTo?: string;
  /** When true, the step renders its own action buttons (hides default Back/Continue) */
  customActions?: boolean;
  /** Identifies the type of custom-action step (e.g. 'quiz' or 'playground') */
  stepType?: 'quiz' | 'playground';
  /** When true, hides the step title below the indicator */
  hideStepTitle?: boolean;
  /** When true, centers the step content */
  centerContent?: boolean;
}

export interface OnboardingSession {
  roleId: string;
  journeyIndex: number;
  steps: ActiveStep[];
  currentStepIndex: number;
  /** Path to navigate back to when session ends */
  returnPath: string;
  /** Called when user completes all steps (not on close/cancel) */
  onComplete?: () => void;
}

interface OnboardingContextValue {
  session: OnboardingSession | null;
  startSession: (session: OnboardingSession) => void;
  endSession: () => void;
  completeSession: () => void;
  nextStep: () => void;
  prevStep: () => void;
  currentStep: ActiveStep | null;
}

const OnboardingContext = createContext<OnboardingContextValue>({
  session: null,
  startSession: () => {},
  endSession: () => {},
  completeSession: () => {},
  nextStep: () => {},
  prevStep: () => {},
  currentStep: null,
});

export const useOnboardingContext = () => useContext(OnboardingContext);

export const OnboardingProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<OnboardingSession | null>(null);

  const startSession = useCallback((s: OnboardingSession) => {
    setSession(s);
  }, []);

  const endSession = useCallback(() => {
    setSession(null);
  }, []);

  const completeSession = useCallback(() => {
    setSession(prev => {
      if (prev?.onComplete) {
        prev.onComplete();
      }
      return null;
    });
  }, []);

  const nextStep = useCallback(() => {
    setSession(prev => {
      if (!prev) return null;
      if (prev.currentStepIndex >= prev.steps.length - 1) return prev;
      return { ...prev, currentStepIndex: prev.currentStepIndex + 1 };
    });
  }, []);

  const prevStep = useCallback(() => {
    setSession(prev => {
      if (!prev || prev.currentStepIndex <= 0) return prev;
      return { ...prev, currentStepIndex: prev.currentStepIndex - 1 };
    });
  }, []);

  const currentStep = session
    ? session.steps[session.currentStepIndex] ?? null
    : null;

  const value = useMemo(
    () => ({ session, startSession, endSession, completeSession, nextStep, prevStep, currentStep }),
    [session, startSession, endSession, completeSession, nextStep, prevStep, currentStep],
  );

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};
